import React, {useCallback, useState} from 'react';
import './CoachingReviewListeningAssistant.styl';
import classNames from 'classnames';
import {PLAYLIST_VISIBILITY} from 'stores/CoachingPlaylistStore';
import CoachingReviewStatusRibbon from 'components/coaching/widgets/CoachingReviewStatusRibbon';
import CoachingContentDetailScore from 'records/CoachingContentDetailScore';
import CoachingReviewFileUpload from 'records/CoachingReviewFileUpload';
import TitleBar from 'components/calls/components/sidebar/listening-assistant/TitleBar';
import {MiniScore} from 'components/calls/components/sidebar/listening-assistant/MiniScore';
import Button from '@material-ui/core/Button';
import {ReactComponent as UTurnIcon} from 'images/icon/u-turn.svg';
import {browserHistory} from 'react-router';
import CoachingPlan from 'records/CoachingPlan';
import {AccordionQuestion} from 'components/calls/components/sidebar/listening-assistant/AccordionQuestion';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import CoachingReview from 'records/CoachingReview';
import InProgressReviewActions from 'actions/coaching/InProgressReviewActions';
import InProgressReview from 'records/InProgressReview';
import {List, Map} from 'immutable';
import User from 'records/User';
import {ReviewId} from 'records/ReviewId';
import {QuestionIdentifier} from 'components/calls/components/sidebar/listening-assistant/QuestionIdentifier';

interface Props {
  plan: CoachingPlan;
  reviewFileUpload: CoachingReviewFileUpload;
  reasonSchemes: any;
  scoringSchemeMap: any;
  questionMap: any;
  onChange: (rfu: CoachingReviewFileUpload) => void;
  inProgress: boolean;
  isPrepping: boolean;
  reviewId: ReviewId;
  review?: CoachingReview;
  sessionUser?: User;
  reviewFileUploads?: List<CoachingReviewFileUpload>;
  reviewTypeAnswers?: Map<any, any>;
}

const CoachingReviewListeningAssistant: React.FC<Props> = ({
  plan,
  reviewFileUpload, // Single rfu/call for listening assistant
  reasonSchemes,
  scoringSchemeMap,
  questionMap,
  onChange,
  inProgress,
  isPrepping,
  reviewId,
  review,
  sessionUser,
  reviewFileUploads, // All rfus to use when start/prep a meeting
  reviewTypeAnswers, // Answers that may have been already provided in previous prep
}) => {
  const [hidden, setHidden] = useState(false);
  const [expandedScore, setExpandedScore] = useState<QuestionIdentifier | null>(null);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  const onUpdateScore = useCallback(
    (coachingContentDetailScore: CoachingContentDetailScore) => {
      const indexOfScore = reviewFileUpload.reviewFeedbackList!.findIndex(
        ccds =>
          ccds.contentDetailId === coachingContentDetailScore.contentDetailId &&
          ccds.reviewFileuploadId === coachingContentDetailScore.reviewFileuploadId &&
          ccds.scorecardQuestionId === coachingContentDetailScore.scorecardQuestionId
      );

      if (indexOfScore === -1) {
        console.error("Can't update the review! This is wrong and bad");
      }

      const updated = reviewFileUpload.setIn(
        ['reviewFeedbackList', indexOfScore],
        coachingContentDetailScore
      );

      onChange(updated);
    },
    [onChange, reviewFileUpload]
  );

  const onQuestionExpandCollapse = useCallback((id: QuestionIdentifier | null) => {
    setUserHasInteracted(true);
    id
      ? setExpandedScore({
          contentDetailId: id.contentDetailId,
          scorecardQuestionId: id.scorecardQuestionId,
        })
      : setExpandedScore(null);
  }, []);

  const onMiniScoreExpand = useCallback(
    (id: QuestionIdentifier) => {
      setHidden(!hidden);
      onQuestionExpandCollapse(id);
    },
    [hidden, onQuestionExpandCollapse]
  );

  const onTitleBarToggle = useCallback(() => setHidden(!hidden), [hidden]);

  const createInProgressReview = (isPrepping: boolean) =>
    new InProgressReview({
      id: reviewId,
      isPrepping: isPrepping,
      scheduleAt: review?.scheduleAt,
      planId: plan.id,
      recipientId: plan.recipient.id,
      reviewFileUploads: reviewFileUploads,
      comment: review?.comment,
      coachingReviewTypeId: Number.isInteger(review?.reviewTypeId)
        ? Number(review?.reviewTypeId)
        : undefined,
      reviewTypeAnswers: reviewTypeAnswers,
      adHoc: plan.adHoc,
    });

  const startMeeting = () => {
    const isCoach = review?.coachUserId === sessionUser?.id;
    CoachingReviewActions.startReview(reviewId, isCoach);

    const inProgressReview = createInProgressReview(false);
    InProgressReviewActions.setInProgressReview(inProgressReview, sessionUser);
  };

  const prepareForMeeting = () => {
    CoachingReviewActions.prepReview(reviewId);

    const inProgressReview = createInProgressReview(true);
    InProgressReviewActions.setInProgressReview(inProgressReview, sessionUser);
  };

  return (
    <div className={classNames('coaching-listening-assistant', hidden ? 'peek' : 'shown')}>
      <TitleBar
        visibility={hidden ? PLAYLIST_VISIBILITY.peek : PLAYLIST_VISIBILITY.shown}
        onToggle={onTitleBarToggle}
      />
      {!hidden && (
        <CoachingReviewStatusRibbon isPrepping={isPrepping} inProgress={inProgress} narrow>
          <Button color="primary" className={'back-button'} onClick={browserHistory.goBack}>
            <UTurnIcon /> Back to Meeting
          </Button>
        </CoachingReviewStatusRibbon>
      )}
      {!hidden && !inProgress && !isPrepping && review && (
        <div>
          <div className="p1">
            <Button fullWidth variant="outlined" onClick={startMeeting}>
              Start Meeting
            </Button>
          </div>
          <div className="p1">
            <Button fullWidth variant="outlined" onClick={prepareForMeeting}>
              Prepare for Meeting
            </Button>
          </div>
        </div>
      )}
      {hidden ? (
        <MiniScore
          scores={reviewFileUpload.reviewFeedbackList!}
          onExpand={onMiniScoreExpand}
          scoringSchemeMap={scoringSchemeMap}
          questionMap={questionMap}
          recipient={plan.recipient!.info!}
        />
      ) : (
        <div className="accordion-wrapper">
          {reviewFileUpload.reviewFeedbackList!.map((contentDetailScore, j) => {
            const expanded =
              (expandedScore &&
                contentDetailScore.contentDetailId === expandedScore.contentDetailId &&
                contentDetailScore.scorecardQuestionId === expandedScore.scorecardQuestionId) ??
              (j === 0 && expandedScore === null && !userHasInteracted);

            return (
              <AccordionQuestion
                contentDetailScore={contentDetailScore}
                onQuestionExpandCollapse={onQuestionExpandCollapse}
                onUpdateScore={onUpdateScore}
                expanded={expanded}
                plan={plan}
                questionMap={questionMap}
                scoringSchemeMap={scoringSchemeMap}
                reasonSchemes={reasonSchemes}
                inProgress={inProgress}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachingReviewListeningAssistant;
